import { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { AreaChip } from "components/manualReview/components/AreaChip";
import { MaterialChip } from "components/manualReview/components/MaterialChip";
import { UnknownChip } from "components/manualReview/components/UnknownChip";
import { useAreaLabel } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import {
  DIMENSION_AREA,
  DIMENSION_EVENT_TYPE,
  DIMENSION_MATERIAL,
  EVENT_TYPE_VALUES,
  MaterialAreaFilter,
  UNKNOWN_LABEL_FILTER_VALUE,
} from "components/manualReview/filters/eventFilters";
import { LimitToggle } from "components/manualReview/filters/LimitToggle";
import { filterPillSx } from "components/manualReview/filters/filterPillSx";
import { useTenantTranslation } from "hooks/formatters";
import {
  EventType,
  FilterDimension,
  MaterialRead,
} from "src/store/api/generatedApi";

const chipGridSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  p: 1,
  maxWidth: 360,
} as const;

const dimensionSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
} as const;

const IS = "is" as const;
const IS_NOT = "is_not" as const;

const NegateToggle = ({
  negated,
  onChange,
}: {
  negated: boolean;
  onChange: (negated: boolean) => void;
}) => {
  const { t } = useTenantTranslation();
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={negated ? IS_NOT : IS}
      onChange={(_, value: typeof IS | typeof IS_NOT | null) => {
        if (value !== null) onChange(value === IS_NOT);
      }}
    >
      <ToggleButton value={IS}>{t("filterIs")}</ToggleButton>
      <ToggleButton value={IS_NOT}>{t("filterIsNot")}</ToggleButton>
    </ToggleButtonGroup>
  );
};

export const MaterialAreaFilterPill = ({
  filter,
  materials,
  areas,
  location,
  onChange,
  onRemove,
}: {
  filter: MaterialAreaFilter;
  materials: MaterialRead[];
  areas: string[];
  location: string | undefined;
  onChange: (filter: MaterialAreaFilter) => void;
  onRemove: () => void;
}) => {
  const { t } = useTenantTranslation();
  const resolveAreaLabel = useAreaLabel(location);
  const materialAnchorRef = useRef<HTMLDivElement>(null);
  const areaAnchorRef = useRef<HTMLDivElement>(null);
  const eventTypeAnchorRef = useRef<HTMLDivElement>(null);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [eventTypeOpen, setEventTypeOpen] = useState(false);

  const selectedMaterial =
    materials.find((material) => material.name === filter.material) ?? null;

  const withoutDimension = (dimension: FilterDimension) =>
    (filter.negated ?? []).filter((d) => d !== dimension);

  const setDimensionNegated = (
    dimension: FilterDimension,
    negated: boolean
  ) => {
    const without = withoutDimension(dimension);
    onChange({
      ...filter,
      negated: negated ? [...without, dimension] : without,
    });
  };

  const selectMaterial = (material: string | null) => {
    onChange({
      ...filter,
      material,
      negated:
        material === null
          ? withoutDimension(DIMENSION_MATERIAL)
          : filter.negated,
    });
    setMaterialOpen(false);
  };
  const selectArea = (area: string | null) => {
    onChange({
      ...filter,
      area,
      negated:
        area === null ? withoutDimension(DIMENSION_AREA) : filter.negated,
    });
    setAreaOpen(false);
  };
  const selectEventType = (eventType: EventType | null) => {
    onChange({
      ...filter,
      eventType,
      negated:
        eventType === null
          ? withoutDimension(DIMENSION_EVENT_TYPE)
          : filter.negated,
    });
    setEventTypeOpen(false);
  };

  return (
    <Box sx={filterPillSx}>
      <Box sx={dimensionSx}>
        <Box ref={materialAnchorRef} sx={{ display: "inline-flex" }}>
          {filter.material === UNKNOWN_LABEL_FILTER_VALUE ? (
            <UnknownChip
              selected
              onClick={() => {
                setMaterialOpen(true);
              }}
            />
          ) : selectedMaterial ? (
            <MaterialChip
              material={selectedMaterial}
              selected
              onClick={() => {
                setMaterialOpen(true);
              }}
            />
          ) : (
            <Chip
              label={t("anyMaterial")}
              variant="outlined"
              onClick={() => {
                setMaterialOpen(true);
              }}
            />
          )}
        </Box>
        {filter.material !== null && (
          <NegateToggle
            negated={(filter.negated ?? []).includes(DIMENSION_MATERIAL)}
            onChange={(negated) => {
              setDimensionNegated(DIMENSION_MATERIAL, negated);
            }}
          />
        )}
      </Box>
      <Menu
        anchorEl={materialAnchorRef.current}
        open={materialOpen}
        onClose={() => {
          setMaterialOpen(false);
        }}
      >
        <Box sx={chipGridSx}>
          <Chip
            label={t("anyMaterial")}
            variant={filter.material === null ? "filled" : "outlined"}
            onClick={() => {
              selectMaterial(null);
            }}
          />
          <UnknownChip
            selected={filter.material === UNKNOWN_LABEL_FILTER_VALUE}
            onClick={() => {
              selectMaterial(UNKNOWN_LABEL_FILTER_VALUE);
            }}
          />
          {materials.map((material) => (
            <MaterialChip
              key={material.name}
              material={material}
              selected={filter.material === material.name}
              onClick={() => {
                selectMaterial(material.name);
              }}
            />
          ))}
        </Box>
      </Menu>

      <Box sx={dimensionSx}>
        <Box ref={areaAnchorRef} sx={{ display: "inline-flex" }}>
          {filter.area === null ? (
            <Chip
              label={t("anyArea")}
              variant="outlined"
              onClick={() => {
                setAreaOpen(true);
              }}
            />
          ) : filter.area === UNKNOWN_LABEL_FILTER_VALUE ? (
            <UnknownChip
              selected
              onClick={() => {
                setAreaOpen(true);
              }}
            />
          ) : (
            <AreaChip
              area={filter.area}
              label={resolveAreaLabel(filter.area)}
              selected
              onClick={() => {
                setAreaOpen(true);
              }}
            />
          )}
        </Box>
        {filter.area !== null && (
          <NegateToggle
            negated={(filter.negated ?? []).includes(DIMENSION_AREA)}
            onChange={(negated) => {
              setDimensionNegated(DIMENSION_AREA, negated);
            }}
          />
        )}
      </Box>
      <Menu
        anchorEl={areaAnchorRef.current}
        open={areaOpen}
        onClose={() => {
          setAreaOpen(false);
        }}
      >
        <Box sx={chipGridSx}>
          <Chip
            label={t("anyArea")}
            variant={filter.area === null ? "filled" : "outlined"}
            onClick={() => {
              selectArea(null);
            }}
          />
          <UnknownChip
            selected={filter.area === UNKNOWN_LABEL_FILTER_VALUE}
            onClick={() => {
              selectArea(UNKNOWN_LABEL_FILTER_VALUE);
            }}
          />
          {areas.map((area) => (
            <AreaChip
              key={area}
              area={area}
              label={resolveAreaLabel(area)}
              selected={filter.area === area}
              onClick={() => {
                selectArea(area);
              }}
            />
          ))}
        </Box>
      </Menu>

      <Box sx={dimensionSx}>
        <Box ref={eventTypeAnchorRef} sx={{ display: "inline-flex" }}>
          <Chip
            label={
              filter.eventType !== null
                ? filter.eventType.toUpperCase()
                : t("anyEventType")
            }
            variant={filter.eventType !== null ? "filled" : "outlined"}
            onClick={() => {
              setEventTypeOpen(true);
            }}
          />
        </Box>
        {filter.eventType !== null && (
          <NegateToggle
            negated={(filter.negated ?? []).includes(DIMENSION_EVENT_TYPE)}
            onChange={(negated) => {
              setDimensionNegated(DIMENSION_EVENT_TYPE, negated);
            }}
          />
        )}
      </Box>
      <Menu
        anchorEl={eventTypeAnchorRef.current}
        open={eventTypeOpen}
        onClose={() => {
          setEventTypeOpen(false);
        }}
      >
        <Box sx={chipGridSx}>
          <Chip
            label={t("anyEventType")}
            variant={filter.eventType === null ? "filled" : "outlined"}
            onClick={() => {
              selectEventType(null);
            }}
          />
          {EVENT_TYPE_VALUES.map((eventType) => (
            <Chip
              key={eventType}
              label={eventType.toUpperCase()}
              variant={filter.eventType === eventType ? "filled" : "outlined"}
              onClick={() => {
                selectEventType(eventType);
              }}
            />
          ))}
        </Box>
      </Menu>

      <LimitToggle
        limitTo={filter.limitTo}
        onChange={(limitTo) => {
          onChange({ ...filter, limitTo });
        }}
      />

      <IconButton
        size="small"
        aria-label={t("removeFilter")}
        onClick={onRemove}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
